// SEQ payment-claim summary - client-side mirror of the edge function's
// buildPaymentSummaryRows (supabase/functions/generate-progress-claim/summary-rows.ts).
//
// The app cannot import across into supabase/functions, so this file
// duplicates the row-building logic verbatim, exactly like src/lib/claimSummary.ts
// mirrors claim-summary.ts. A parity test (summaryRows.test.ts) imports BOTH
// modules and asserts identical row arrays on every fixture, so the two
// copies cannot drift apart silently.
//
// Layout (Josh's mockup): splits the old combined "previously certified"
// deduction into its own bold subtotal, Total Certified (B = F + R), with
// the receipts/retention-held rows nested under it, and adds a second bold
// subtotal, Total this claim excluding retention (D = C - H [+ Release]),
// so the GST base is a visible line instead of implied. D equals
// totalExcGst from claimSummary.ts's computeClaimSummary: G = D x 10%,
// T = D + G. This module does no money math beyond combining figures the
// engine already produced - it is presentation only, pure, no pdf-lib, no DOM.

import { retentionCeilingStatus } from "./claimSummary";
import { formatRetentionCeilingPercent } from "./retentionCeiling";

export interface SummaryRow {
  label: string;
  amount: number;
  bold?: boolean;
  total?: boolean;
  muted?: boolean;
  /**
   * Josh's mockup: the Schedule 1/3/2 rows and the ceiling row are indented
   * under their parent total, but Funds Receipted / Retention Previously
   * Held (nested under Total Certified) are NOT - they sit flush left like
   * a main row, just muted grey. muted drives colour only; indent drives
   * the label's x-position.
   */
  indent?: boolean;
  tag?: string;
}

export interface BuildPaymentSummaryRowsInput {
  A: number;
  /**
   * Amendment 2 deduction split. Both present => the split rows (Funds
   * Receipted / Retention Previously Held) render under Total Certified.
   * Either null/undefined => legacy combined layout using B directly
   * (reports frozen before the split; never derive the split from a frozen
   * B - see src/lib/page1Summary.ts hasDeductionSplit).
   */
  bReceipts: number | null;
  retentionHeldToDate: number | null;
  /** Legacy combined certified figure. Used only when the split is absent. */
  B: number;
  C: number;
  P: number;
  installHire: number;
  V: number;
  R: number;
  release: number;
  G: number;
  T: number;
  accumulatedRetention: number | null;
  retentionCeiling: number | null;
  // SEQ-235: the project's ceiling percentage for the wording. Absent on older frozen reports, which read as 5.
  retentionCeilingPercent?: number | null;
  /** The two renderers format currency slightly differently; caller supplies theirs. */
  formatCurrency: (amount: number) => string;
}

/**
 * Builds the PAYMENT CLAIM SUMMARY rows top to bottom. Rows 6/7/8 (Schedule
 * 1/3/2) are informational references to the schedules, NOT addends of C -
 * never assert C == P + V (false when priors are unpaid).
 */
export function buildPaymentSummaryRows(input: BuildPaymentSummaryRowsInput): SummaryRow[] {
  const hasSplit = input.bReceipts != null && input.retentionHeldToDate != null;

  const rows: SummaryRow[] = [
    { label: "Total claimed to date", amount: input.A, tag: "A" },
  ];

  if (hasSplit) {
    const bReceipts = input.bReceipts as number;
    const retentionHeldToDate = input.retentionHeldToDate as number;
    rows.push({
      label: "Total Certified",
      amount: bReceipts + retentionHeldToDate,
      bold: true,
      tag: "B = F + R",
    });
    rows.push({ label: "Funds Receipted", amount: -bReceipts, muted: true, tag: "F" });
    rows.push({
      label: "Retention Previously Held",
      amount: -retentionHeldToDate,
      muted: true,
      tag: "R",
    });
  } else {
    rows.push({ label: "Total Certified", amount: input.B, bold: true, tag: "B" });
  }

  rows.push({ label: "Total this claim excluding GST", amount: input.C, bold: true, tag: "C = A - B" });
  rows.push({
    label: "Progress works this claim",
    amount: input.P,
    muted: true,
    indent: true,
    tag: "Schedule 1",
  });
  if (Math.abs(input.installHire) >= 0.01) {
    rows.push({
      label: "Install & Hire this claim",
      amount: input.installHire,
      muted: true,
      indent: true,
      tag: "Schedule 3",
    });
  }
  rows.push({
    label: "Variations this claim",
    amount: input.V,
    muted: true,
    indent: true,
    tag: "Schedule 2",
  });
  rows.push({ label: "Less retention held this claim", amount: -input.R, tag: "H" });

  if (input.retentionCeiling != null && input.retentionCeiling > 0) {
    const pctLabel = formatRetentionCeilingPercent(input.retentionCeilingPercent);
    const ceilingStatus = retentionCeilingStatus(
      input.accumulatedRetention ?? 0,
      input.R,
      input.retentionCeiling,
    );
    rows.push({
      label:
        `Retention held to date (incl. this claim) of ${input.formatCurrency(ceilingStatus.ceiling)} (${pctLabel}% ceiling)` +
        (ceilingStatus.overCeiling ? ` - EXCEEDS ${pctLabel}% CEILING` : ""),
      amount: ceilingStatus.heldToDate,
      muted: true,
      indent: true,
    });
  }

  const hasRelease = input.release > 0.005;
  if (hasRelease) {
    rows.push({ label: "Plus retention release", amount: input.release });
  }

  rows.push({
    label: "Total this claim excluding retention",
    amount: input.C - input.R + input.release,
    bold: true,
    tag: hasRelease ? "D = C - H + Release" : "D = C - H",
  });

  rows.push({ label: "GST", amount: input.G });
  rows.push({ label: "TOTAL PAYABLE", amount: input.T, bold: true, total: true });

  return rows;
}
